import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats, testimonials, faqs } from '@/mocks/movies';

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <Navbar />

      <main>
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' }
              ]}
            />
          </div>
        </div>

        {/* Page Header */}
        <section className="py-12 md:py-16 bg-background-50 text-center">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">关于屁屁影院</h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
              致力于为全球华语用户提供免费、高清、流畅的在线观影体验
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-100" aria-label="品牌介绍">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-600 rounded-full mb-4 whitespace-nowrap">
                  品牌故事
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">品牌介绍</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  屁屁影院创立于2020年，是一家专注于提供免费高清影视在线观看服务的平台。我们始终坚持「免费、高清、流畅」的产品理念，致力于为全球华语用户打造极致的观影体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  经过多年的发展，屁屁影院已积累了超过50000部电影资源，涵盖动作、喜剧、科幻、爱情、悬疑、动画等多种类型，满足了不同用户的观影需求。我们拥有一支专业的技术团队，通过先进的CDN加速技术和智能码率调节算法，确保每一位用户都能享受到流畅、清晰的观影体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  未来，屁屁影院将继续秉持初心，不断扩充片源库，优化播放技术，让更多人能够轻松享受电影的乐趣。
                </p>
              </div>
              <div className="relative">
                <div className="aspect-video rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20cinema%20home%20theater%20setup%20with%20large%20screen%20displaying%20movie%2C%20comfortable%20sofa%2C%20warm%20ambient%20lighting%2C%20cozy%20entertainment%20room%2C%20cinematic%20home%20experience%2C%20warm%20atmosphere%2C%20modern%20interior%20design%2C%20photorealistic&width=800&height=450&seq=about-brand&orientation=landscape"
                    alt="屁屁影院观影体验"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-background-50" aria-label="数据一览">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">数据一览</h2>
              <p className="text-sm text-foreground-500">用数据说话，见证我们的成长</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100" aria-label="用户口碑">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">用户口碑</h2>
              <p className="text-sm text-foreground-500">来自千万用户的真实评价</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map(t => (
                <div key={t.id} className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-50" aria-label="常见问题">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">常见问题</h2>
              <p className="text-sm text-foreground-500">关于屁屁影院的常见疑问解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-100" aria-label="联系我们">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">联系我们</h2>
              <p className="text-sm text-foreground-500">如有任何问题或建议，欢迎通过以下方式联系我们</p>
            </div>
            <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mx-auto mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-800">contact@pipicinema.com</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500 mx-auto mb-3">
                  <i className="ri-wechat-line text-xl"></i>
                </span>
                <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                <p className="text-sm font-medium text-foreground-800">屁屁影院</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 mx-auto mb-3">
                  <i className="ri-weibo-line text-xl"></i>
                </span>
                <p className="text-xs text-foreground-500 mb-1">官方微博</p>
                <p className="text-sm font-medium text-foreground-800">@屁屁影院官方</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mx-auto mb-3">
                  <i className="ri-map-pin-line text-xl"></i>
                </span>
                <p className="text-xs text-foreground-500 mb-1">公司地址</p>
                <p className="text-sm font-medium text-foreground-800">北京市朝阳区建国路88号</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于屁屁影院",
            "description": "屁屁影院创立于2020年，是一家专注于提供免费高清影视在线观看服务的平台。了解我们的品牌故事、用户口碑和联系方式。",
            "url": "https://www.pipicinema.com/about",
            "isPartOf": {
              "@type": "WebSite",
              "name": "屁屁影院",
              "url": "https://www.pipicinema.com"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.pipicinema.com" },
                { "@type": "ListItem", "position": 2, "name": "关于我们" }
              ]
            },
            "mainEntity": {
              "@type": "Organization",
              "name": "屁屁影院",
              "url": "https://www.pipicinema.com",
              "description": "屁屁影院作为知名免费影视站，汇聚电影大全，高清免费在线观看不缓冲。",
              "email": "contact@pipicinema.com",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "CN",
                "addressLocality": "北京",
                "streetAddress": "朝阳区建国路88号SOHO现代城A座"
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "bestRating": "5",
                "ratingCount": "12890"
              }
            },
            "faq": {
              "@type": "FAQPage",
              "mainEntity": faqs.map(f => ({
                "@type": "Question",
                "name": f.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": f.answer
                }
              }))
            }
          })
        }}
      />
    </>
  );
}